// src/components/maps/GeographicMap.js
import React, { useEffect, useState, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    IconButton,
    Tooltip,
    Alert
} from '@mui/material';
import {
    MyLocation as MyLocationIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Map as MapIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon
} from '@mui/icons-material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIconShadow from 'leaflet/dist/images/marker-shadow.png';
import { parseCoordinates } from '../../utils/coordinateUtils';

// Corregir el problema de los iconos por defecto en Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: markerIcon,
    shadowUrl: markerIconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

// Pines SVG dinámicos y premium con anclaje 100% preciso en las coordenadas reales
const createSVGIcon = (color, animateClass = '', innerColor = '#ffffff') => {
    return L.divIcon({
        className: 'custom-leaflet-marker',
        html: `
            <div class="custom-svg-marker ${animateClass}" style="position: relative; width: 32px; height: 42px; display: flex; align-items: center; justify-content: center;">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 42" width="32" height="42" style="filter: drop-shadow(0px 3px 5px rgba(0,0,0,0.35));">
                    <!-- Pin Body -->
                    <path d="M16 0C7.16 0 0 7.16 0 16c0 12 16 26 16 26s16-14 16-26C32 7.16 24.84 0 16 0z" fill="${color}" />
                    <!-- Inner dot -->
                    <circle cx="16" cy="16" r="5" fill="${innerColor}" />
                </svg>
            </div>
        `,
        iconSize: [32, 42],
        iconAnchor: [16, 42],
        popupAnchor: [0, -42]
    });
};

// Instanciar los iconos para reutilizarlos en el mapa
const clientActiveIcon = createSVGIcon('#10b981', 'client-active-pulse'); // Verde esmeralda (activo)
const clientInactiveIcon = createSVGIcon('#ef4444'); // Rojo carmín (inactivo)
const pendingInstallationIcon = createSVGIcon('#f59e0b', 'client-pending-pulse'); // Amarillo ámbar (pendiente)
const cancelledInstallationIcon = createSVGIcon('#7f8c8d'); // Gris pizarra (cancelado)

// Icono especial animado para puntos de acceso (AP) con ondas concéntricas de señal
const accessPointIcon = L.divIcon({
    className: 'custom-leaflet-marker',
    html: `
        <div class="custom-svg-marker ap-pulse" style="position: relative; width: 38px; height: 38px; display: flex; align-items: center; justify-content: center; background: #3f51b5; border: 2.5px solid #ffffff; border-radius: 50%; box-shadow: 0px 4px 10px rgba(63, 81, 181, 0.5);">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="#ffffff">
                <path d="M12 3c-4.97 0-9 4.03-9 9 0 2.12.74 4.07 1.97 5.61L4.35 19.4c-1.47-1.92-2.35-4.31-2.35-6.9 0-6.08 4.92-11 11-11s11 4.92 11 11c0 2.59-.88 4.98-2.35 6.9l-1.62-1.79C21.26 16.07 22 14.12 22 12c0-4.97-4.03-9-9-9zm0 4c-2.76 0-5 2.24-5 5 0 1.03.31 1.98.84 2.78l1.49-1.64C9.11 12.74 9 12.38 9 12c0-1.66 1.34-3 3-3s3 1.34 3 3c0 .38-.11.74-.33 1.14l1.49 1.64c.53-.8.84-1.75.84-2.78 0-2.76-2.24-5-5-5zm0 3c-1.1 0-2 .9-2 2 0 .55.22 1.05.59 1.41l1.41-1.41V12h.01l1.39-1.39c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2zm0 5c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/>
            </svg>
            <div class="ap-radar-ring"></div>
            <div class="ap-radar-ring-2"></div>
        </div>
    `,
    iconSize: [38, 38],
    iconAnchor: [19, 19],
    popupAnchor: [0, -19]
});

// Marcador especial de selección (drag & drop) con respiración y mira telescópica (crosshair)
const selectionIcon = L.divIcon({
    className: 'custom-leaflet-marker',
    html: `
        <div class="custom-svg-marker selection-breath" style="position: relative; width: 36px; height: 46px; display: flex; align-items: center; justify-content: center;">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 42" width="36" height="46" style="filter: drop-shadow(0px 6px 8px rgba(139, 92, 246, 0.45));">
                <path d="M16 0C7.16 0 0 7.16 0 16c0 12 16 26 16 26s16-14 16-26C32 7.16 24.84 0 16 0z" fill="#8b5cf6" />
                <circle cx="16" cy="16" r="7" fill="#ffffff" />
                <path d="M16 11v10M11 16h10" stroke="#8b5cf6" stroke-width="2" stroke-linecap="round" />
            </svg>
            <div class="selection-glow"></div>
        </div>
    `,
    iconSize: [36, 46],
    iconAnchor: [18, 46],
    popupAnchor: [0, -46]
});

// Función matemática para calcular los puntos del abanico sectorial en base a la esfericidad terrestre (Haversine)
const getSectorPoints = (center, radiusMeters, azimuth, beamwidth) => {
    const [lat, lng] = center;
    const points = [[lat, lng]];
    const R = 6378137; // Radio terrestre en metros
    const distRatio = radiusMeters / R;
    const latRad = (lat * Math.PI) / 180;
    const lngRad = (lng * Math.PI) / 180;
    
    const startAngle = azimuth - beamwidth / 2;
    const endAngle = azimuth + beamwidth / 2;
    const step = 5; // Resolución en grados del arco
    
    for (let angle = startAngle; angle <= endAngle; angle += step) {
        const bearingRad = (angle * Math.PI) / 180;
        const destLatRad = Math.asin(
            Math.sin(latRad) * Math.cos(distRatio) +
            Math.cos(latRad) * Math.sin(distRatio) * Math.cos(bearingRad)
        );
        const destLngRad = lngRad + Math.atan2(
            Math.sin(bearingRad) * Math.sin(distRatio) * Math.cos(latRad),
            Math.cos(distRatio) - Math.sin(latRad) * Math.sin(destLatRad)
        );
        points.push([(destLatRad * 180) / Math.PI, (destLngRad * 180) / Math.PI]);
    }
    points.push([lat, lng]); // Cerrar polígono
    return points;
};

// Obtener color según RSSI exacto solicitado por el cliente:
// Verde: >-61dBm, Amarillo: [-62 a -69dBm], Rojo: <-70dBm
const getRSSIColor = (rssi) => {
    if (rssi === null || rssi === undefined) return '#94a3b8'; // Slate-Gray para inactivos/sin señal
    if (rssi >= -61) return '#10b981'; // Verde (> -61 dBm)
    if (rssi >= -69) return '#fbbf24'; // Amarillo ([-62 a -69 dBm])
    return '#ef4444'; // Rojo (<-70 dBm)
};

const GeographicMap = ({
    mode = 'clients', // 'clients', 'installations', 'coverage', 'network', 'selection'
    selectedZoneId = null,
    onMarkerClick = null,
    selectedCoordinates = null, // Para modo de selección
    selectedItem = null,
    onCoordinateSelect = null,
    height = '600px',
    hideHeader = false
}) => {
    const mapRef = useRef(null);
    const containerRef = useRef(null); // Ref para el contenedor de pantalla completa
    const mapInstanceRef = useRef(null);
    const markersLayerRef = useRef(null);
    const markerMapRef = useRef(new Map());
    const highlightLayerRef = useRef(null);
    const lastCoordsRef = useRef(null); // Almacena el último estado de coordenadas para erradicar el bucle de salto/reset
    const mapLayersRef = useRef({});

    const [mapReady, setMapReady] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterType, setFilterType] = useState('all'); // 'all', 'active', 'inactive', etc.
    const [zones, setZones] = useState([]);
    const [selectedZone, setSelectedZone] = useState(selectedZoneId || '');
    const [statusCounts, setStatusCounts] = useState({});
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Alternar pantalla completa usando la API nativa del navegador
    const toggleFullscreen = () => {
        if (!containerRef.current) return;

        if (!isFullscreen) {
            const element = containerRef.current;
            if (element.requestFullscreen) {
                element.requestFullscreen();
            } else if (element.mozRequestFullScreen) {
                element.mozRequestFullScreen();
            } else if (element.webkitRequestFullscreen) {
                element.webkitRequestFullscreen();
            } else if (element.msRequestFullscreen) {
                element.msRequestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
    };

    // Sincronizar estado de pantalla completa (por si pulsan Esc)
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement
            );
            setIsFullscreen(isCurrentlyFullscreen);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    // Invalidar tamaño del mapa para recalcular dimensiones tras cambiar de estado
    useEffect(() => {
        if (mapInstanceRef.current) {
            mapInstanceRef.current.invalidateSize();
            const timer = setTimeout(() => {
                if (mapInstanceRef.current) {
                    mapInstanceRef.current.invalidateSize();
                }
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isFullscreen]);

    // Inyectar estilos CSS premium para animaciones de marcadores dinámicos
    useEffect(() => {
        const styleId = 'geographic-map-premium-styles';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = `
                @keyframes apRadarWave {
                    0% {
                        transform: scale(0.6);
                        opacity: 0.8;
                    }
                    100% {
                        transform: scale(2.4);
                        opacity: 0;
                    }
                }
                @keyframes selectionBreath {
                    0%, 100% {
                        transform: translateY(0) scale(1);
                    }
                    50% {
                        transform: translateY(-5px) scale(1.05);
                    }
                }
                @keyframes pulseGlow {
                    0%, 100% {
                        box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.45);
                    }
                    50% {
                        box-shadow: 0 0 0 10px rgba(16, 185, 129, 0);
                    }
                }
                @keyframes pulseGlowPending {
                    0%, 100% {
                        box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.45);
                    }
                    50% {
                        box-shadow: 0 0 0 10px rgba(245, 158, 11, 0);
                    }
                }
                .custom-svg-marker {
                    transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }
                .custom-svg-marker:hover {
                    transform: scale(1.2) translateY(-3px) !important;
                    cursor: grab;
                }
                .custom-svg-marker:active {
                    cursor: grabbing;
                }
                .ap-pulse {
                    position: relative;
                }
                .ap-radar-ring {
                    position: absolute;
                    top: -2.5px;
                    left: -2.5px;
                    width: 38px;
                    height: 38px;
                    border-radius: 50%;
                    border: 2.5px solid #3f51b5;
                    animation: apRadarWave 2.5s infinite linear;
                    pointer-events: none;
                    box-sizing: content-box;
                }
                .ap-radar-ring-2 {
                    position: absolute;
                    top: -2.5px;
                    left: -2.5px;
                    width: 38px;
                    height: 38px;
                    border-radius: 50%;
                    border: 2.5px solid #5c6bc0;
                    animation: apRadarWave 2.5s infinite linear;
                    animation-delay: 1.25s;
                    pointer-events: none;
                    box-sizing: content-box;
                }
                .selection-breath {
                    animation: selectionBreath 2.5s infinite ease-in-out;
                }
                .selection-glow {
                    position: absolute;
                    width: 16px;
                    height: 16px;
                    background: rgba(139, 92, 246, 0.4);
                    border-radius: 50%;
                    filter: blur(4px);
                    z-index: -1;
                    bottom: 0;
                }
                .client-active-pulse {
                    border-radius: 50%;
                    animation: pulseGlow 2s infinite ease-in-out;
                }
                .client-pending-pulse {
                    border-radius: 50%;
                    animation: pulseGlowPending 2s infinite ease-in-out;
                }
            `;
            document.head.appendChild(style);
        }
    }, []);

    useEffect(() => {
        setSelectedZone(selectedZoneId || '');
    }, [selectedZoneId]);

    // Manejar lógica reactiva según el modo (clic en mapa y cambio de capa base por defecto)
    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!map || !mapReady) return;

        // 1. Manejar el evento de clic para selección de coordenadas
        map.off('click');

        if (mode === 'selection') {
            const handleMapClick = (e) => {
                markersLayerRef.current.clearLayers();

                const marker = L.marker(e.latlng, {
                    draggable: true,
                    icon: selectionIcon
                }).addTo(markersLayerRef.current);

                lastCoordsRef.current = [e.latlng.lat, e.latlng.lng];
                if (onCoordinateSelect) {
                    onCoordinateSelect(`${e.latlng.lat.toFixed(6)}, ${e.latlng.lng.toFixed(6)}`);
                }

                marker.on('dragend', function (dragEvent) {
                    const position = dragEvent.target.getLatLng();
                    lastCoordsRef.current = [position.lat, position.lng];
                    if (onCoordinateSelect) {
                        onCoordinateSelect(`${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`);
                    }
                });
            };

            map.on('click', handleMapClick);

            return () => {
                map.off('click', handleMapClick);
            };
        }
    }, [mapReady, mode, onCoordinateSelect]);

    useEffect(() => {
        fetchZones();
        initializeMap();

        // Robust sizing with ResizeObserver to prevent Leaflet container size 0/blank bugs inside Dialogs/Modals/Tabs
        let resizeObserver = null;
        if (typeof ResizeObserver !== 'undefined' && mapRef.current) {
            resizeObserver = new ResizeObserver(() => {
                if (mapInstanceRef.current) {
                    mapInstanceRef.current.invalidateSize();
                }
            });
            resizeObserver.observe(mapRef.current);
        }

        // Invalida tamaño con retraso de seguridad para transiciones animadas de Diálogos/modales
        const sizeTimer = setTimeout(() => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.invalidateSize();
            }
        }, 250);

        return () => {
            clearTimeout(sizeTimer);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (mapInstanceRef.current) {
            // Invalidar tamaño con delay ligero para re-cálculo correcto en Grid
            setTimeout(() => {
                if (mapInstanceRef.current) {
                    mapInstanceRef.current.invalidateSize();
                }
            }, 150);
            loadMarkers();
        }
    }, [mode, filterType, selectedZone]);

    // Escucha y centrado fluido de coordenadas seleccionadas con auto-apertura de popup y aro de resaltado
    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!map) return;

        if (selectedCoordinates) {
            const [lat, lng] = parseCoordinates(selectedCoordinates);

            if (lat && lng) {
                const lastCoords = lastCoordsRef.current;
                const diff = lastCoords ? Math.hypot(lat - lastCoords[0], lng - lastCoords[1]) : Infinity;

                if (diff > 1e-6) {
                    lastCoordsRef.current = [lat, lng];

                    // Centrado suave en la ubicación seleccionada a nivel de casa/predio (Zoom 18)
                    const currentZoom = map.getZoom();
                    const targetZoom = currentZoom > 17 ? currentZoom : 18;
                    map.flyTo([lat, lng], targetZoom, {
                        animate: true,
                        duration: 1.2
                    });
                }

                // Modo Selección Manual (drag & drop)
                if (mode === 'selection' && markersLayerRef.current) {
                    markersLayerRef.current.clearLayers();
                    const marker = L.marker([lat, lng], {
                        draggable: true,
                        icon: selectionIcon
                    }).addTo(markersLayerRef.current);

                    marker.on('dragend', function (e) {
                        const position = e.target.getLatLng();
                        lastCoordsRef.current = [position.lat, position.lng];
                        if (onCoordinateSelect) {
                            onCoordinateSelect(`${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`);
                        }
                    });
                    return;
                }

                // Crear o limpiar capa de aro de resaltado dinámico (Púrpura/Azul neón pulsante)
                if (!highlightLayerRef.current) {
                    highlightLayerRef.current = L.layerGroup().addTo(map);
                }
                highlightLayerRef.current.clearLayers();

                L.circle([lat, lng], {
                    radius: 12,
                    color: '#6366f1',
                    fillColor: '#818cf8',
                    fillOpacity: 0.45,
                    weight: 3,
                    className: 'client-active-pulse'
                }).addTo(highlightLayerRef.current);

                // Buscar el marcador correspondiente y ABRIR POPUP de forma automática
                let matchedMarker = null;
                if (selectedItem && selectedItem.id && markerMapRef.current.has(String(selectedItem.id))) {
                    matchedMarker = markerMapRef.current.get(String(selectedItem.id));
                } else {
                    const coordKey = `${lat.toFixed(5)},${lng.toFixed(5)}`;
                    if (markerMapRef.current.has(coordKey)) {
                        matchedMarker = markerMapRef.current.get(coordKey);
                    } else {
                        let minDist = Infinity;
                        markerMapRef.current.forEach((m) => {
                            const pos = m.getLatLng();
                            const dist = Math.hypot(pos.lat - lat, pos.lng - lng);
                            if (dist < minDist) {
                                minDist = dist;
                                matchedMarker = m;
                            }
                        });
                        if (minDist > 0.003) matchedMarker = null;
                    }
                }

                if (matchedMarker) {
                    matchedMarker.setZIndexOffset(2000);
                    setTimeout(() => {
                        if (matchedMarker && mapInstanceRef.current) {
                            matchedMarker.openPopup();
                        }
                    }, 350);
                }
            }
        } else {
            if (highlightLayerRef.current) {
                highlightLayerRef.current.clearLayers();
            }
            if (mode === 'selection' && markersLayerRef.current) {
                markersLayerRef.current.clearLayers();
                lastCoordsRef.current = null;
            }
        }
    }, [selectedCoordinates, selectedItem, mode]);

    const fetchZones = async () => {
        try {
            const response = await fetch('/api/zones', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    window.location.href = '/login';
                    return;
                }
                throw new Error('Error al cargar zonas');
            }

            const data = await response.json();
            setZones(data);
        } catch (error) {
            console.error('Error fetching zones:', error);
            setError('No se pudieron cargar las zonas');
        }
    };

    const initializeMap = () => {
        if (!mapRef.current) return;

        try {
            // Coordenadas de inicio por defecto: Sullana, Piura, Perú
            let initialCenter = [-4.9038, -80.6851];
            let initialZoom = 13;

            // Si hay coordenadas válidas pre-seleccionadas, utilizarlas en su lugar
            if (selectedCoordinates) {
                const [lat, lng] = parseCoordinates(selectedCoordinates);
                if (lat && lng) {
                    initialCenter = [lat, lng];
                    initialZoom = 15;
                    lastCoordsRef.current = [lat, lng];
                }
            }

            const osm = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
                maxZoom: 22,
                maxNativeZoom: 19
            });

            const googleHybrid = L.tileLayer('https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
                subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
                attribution: '&copy; Google Maps',
                maxZoom: 22,
                maxNativeZoom: 20
            });

            const esriSatellite = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
                attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
                maxZoom: 22,
                maxNativeZoom: 17
            });

            const cartoDark = L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
                subdomains: 'abcd',
                maxZoom: 22,
                maxNativeZoom: 19
            });

            const defaultLayer = googleHybrid;

            const map = L.map(mapRef.current, {
                center: initialCenter,
                zoom: initialZoom,
                maxZoom: 22,
                layers: [defaultLayer]
            });

            mapInstanceRef.current = map;
            markersLayerRef.current = L.layerGroup().addTo(map);

            const baseLayers = {
                "Satelital Google (Híbrido)": googleHybrid,
                "Satelital Esri": esriSatellite,
                "Mapa Oscuro (Neón)": cartoDark,
                "Mapa Estándar": osm
            };
            L.control.layers(baseLayers, null, { position: 'topright' }).addTo(map);

            // Guardar referencias a las capas para alternar dinámicamente
            mapLayersRef.current = {
                osm,
                googleHybrid,
                esriSatellite,
                cartoDark
            };

            L.control.scale({ imperial: false }).addTo(map);

            loadMarkers().catch(err => {
                console.error("Error cargando marcadores:", err);
                setError("No se pudieron cargar los marcadores: " + err.message);
            });
            setMapReady(true);
        } catch (error) {
            console.error("Error inicializando mapa:", error);
            setError("Error al inicializar el mapa: " + error.message);
        }
    };

    const loadMarkers = async () => {
        if (!mapInstanceRef.current || !markersLayerRef.current) return;

        setLoading(true);
        setError(null);

        markersLayerRef.current.clearLayers();
        markerMapRef.current.clear();
        if (highlightLayerRef.current) {
            highlightLayerRef.current.clearLayers();
        }

        try {
            let endpoint;
            let params = new URLSearchParams();

            if (selectedZone) {
                params.append('zone', selectedZone);
            }

            if (filterType !== 'all') {
                params.append('status', filterType);
            }

            switch (mode) {
                case 'clients':
                    endpoint = `/api/client-ap-connections?${params.toString()}`;
                    break;
                case 'installations':
                    endpoint = `/api/installations/map?${params.toString()}`;
                    break;
                case 'coverage':
                    endpoint = `/api/access-points/coverage?${params.toString()}`;
                    break;
                case 'network':
                    endpoint = `/api/network/topology?${params.toString()}`;
                    break;
                case 'selection':
                    // En modo selección renderizamos únicamente el marcador manual
                    setLoading(false);
                    if (selectedCoordinates) {
                        const [lat, lng] = parseCoordinates(selectedCoordinates);
                        if (lat && lng) {
                            const marker = L.marker([lat, lng], {
                                draggable: true,
                                icon: selectionIcon
                            }).addTo(markersLayerRef.current);

                            marker.on('dragend', function (e) {
                                const position = e.target.getLatLng();
                                lastCoordsRef.current = [position.lat, position.lng];
                                if (onCoordinateSelect) {
                                    onCoordinateSelect(`${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`);
                                }
                            });
                        }
                    }
                    return;
                default:
                    throw new Error('Modo de mapa no válido');
            }

            const headers = {};
            const token = localStorage.getItem('token');
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(endpoint, { headers });

            if (response.status === 401) {
                window.location.href = '/login';
                return;
            }

            if (!response.ok) {
                throw new Error('Error al cargar datos del mapa');
            }

            const data = await response.json();

            // Verificar si el resultado está vacío según el modo
            const isEmpty = mode === 'clients'
                ? (!data || !data.clients || data.clients.length === 0)
                : (!data || data.length === 0);

            if (isEmpty) {
                setStatusCounts({});
                setError('No hay elementos para mostrar en esta zona o filtro.');
                setLoading(false);
                return;
            }

            const counts = {};

            switch (mode) {
                case 'clients': {
                    const { clients, accessPoints } = data;

                    // 1. Mapear Puntos de Acceso para dibujarlos
                    const apMap = new Map();
                    accessPoints.forEach(ap => {
                        let apLat = null;
                        let apLng = null;

                        if (ap.coordinates) {
                            if (typeof ap.coordinates === 'object') {
                                apLat = ap.coordinates.lat;
                                apLng = ap.coordinates.lng;
                            } else {
                                const [parsedLat, parsedLng] = parseCoordinates(ap.coordinates);
                                apLat = parsedLat;
                                apLng = parsedLng;
                            }
                        }

                        if (apLat && apLng) {
                            apMap.set(ap.id, [apLat, apLng]);

                            // Dibujar marcador de AP
                            const apMarker = L.marker([apLat, apLng], { icon: accessPointIcon })
                                .addTo(markersLayerRef.current)
                                .bindPopup(`
                                    <div style="font-family: 'Outfit', sans-serif; padding: 6px;">
                                        <h3 style="margin: 0 0 6px 0; color: #3f51b5; font-size: 14px; font-weight: 600;">AP: ${ap.name}</h3>
                                        <p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>Modelo:</strong> ${ap.model || 'N/A'}</p>
                                        <p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>IP:</strong> ${ap.ip || 'N/A'}</p>
                                        <p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>Clientes Activos:</strong> ${ap.client_count || 0}</p>
                                    </div>
                                `);

                            if (ap.id) markerMapRef.current.set(String(ap.id), apMarker);
                            markerMapRef.current.set(`${apLat.toFixed(5)},${apLng.toFixed(5)}`, apMarker);
                        }
                    });

                    // 2. Mapear Clientes y Enlaces
                    clients.forEach(client => {
                        let clientLat = null;
                        let clientLng = null;

                        if (client.coordinates) {
                            if (typeof client.coordinates === 'object') {
                                clientLat = client.coordinates.lat;
                                clientLng = client.coordinates.lng;
                            } else {
                                const [parsedLat, parsedLng] = parseCoordinates(client.coordinates);
                                clientLat = parsedLat;
                                clientLng = parsedLng;
                            }
                        }

                        if (clientLat && clientLng) {
                            counts[client.status] = (counts[client.status] || 0) + 1;

                            // Dibujar marcador de Cliente
                            const icon = client.status === 'active' ? clientActiveIcon : clientInactiveIcon;
                            const marker = L.marker([clientLat, clientLng], { icon })
                                .addTo(markersLayerRef.current)
                                .bindPopup(`
                                    <div style="font-family: 'Outfit', sans-serif; padding: 6px;">
                                        <h3 style="margin: 0 0 6px 0; color: #1a202c; font-size: 14px; font-weight: 600;">${client.fullName}</h3>
                                        <p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>Plan:</strong> ${client.planName || 'N/A'}</p>
                                        <p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>IP:</strong> ${client.ipAddress || 'N/A'}</p>
                                        <p style="margin: 3px 0; font-size: 12px;"><strong>Estado:</strong> 
                                            <span style="color: ${client.status === 'active' ? '#10b981' : '#ef4444'}; font-weight: 600;">
                                                ${client.status === 'active' ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </p>
                                        ${client.rssi !== null && client.status === 'active' ? `
                                            <p style="margin: 3px 0; font-size: 12px;"><strong>Señal Ubiquiti:</strong> 
                                                <span style="color: ${getRSSIColor(client.rssi)}; font-weight: 700;">
                                                    ${client.rssi} dBm
                                                </span>
                                            </p>
                                            ${client.ccq ? `<p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>CCQ Ubiquiti:</strong> ${client.ccq}%</p>` : ''}
                                            ${client.latency !== null ? `<p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>Latencia Ping:</strong> ${client.latency} ms</p>` : ''}
                                            ${client.txRate ? `<p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>Velocidad TX/RX:</strong> ${client.txRate}/${client.rxRate || '-'} Mbps</p>` : ''}
                                            <p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>Distancia:</strong> ${client.distance} km</p>
                                        ` : ''}
                                        ${!client.accessPointId ? `
                                            <p style="margin: 3px 0; font-size: 12px; color: #d97706; font-weight: 600;">⚠️ Sin AP asignado</p>
                                        ` : !apMap.has(client.accessPointId) ? `
                                            <p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>AP Conectado:</strong> ${client.accessPoint?.name || 'N/A'}</p>
                                            <p style="margin: 3px 0; font-size: 12px; color: #d97706; font-weight: 600;">⚠️ AP sin coordenadas válidas</p>
                                        ` : `
                                            <p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>AP Conectado:</strong> ${client.accessPoint?.name || 'N/A'}</p>
                                        `}
                                        <p style="margin: 3px 0; font-size: 12px; color: #4a5568; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 200px;"><strong>Dirección:</strong> ${client.address || 'N/A'}</p>
                                    </div>
                                `);

                            if (client.id) markerMapRef.current.set(String(client.id), marker);
                            markerMapRef.current.set(`${clientLat.toFixed(5)},${clientLng.toFixed(5)}`, marker);

                            if (onMarkerClick) {
                                marker.on('click', () => onMarkerClick(client));
                            }

                            // 3. Dibujar enlace de conexión Ubiquiti
                            if (client.accessPointId && apMap.has(client.accessPointId)) {
                                const apCoords = apMap.get(client.accessPointId);
                                const isLinkOnline = client.linkStatus === 'online' || (client.rssi !== null);
                                const linkColor = isLinkOnline ? getRSSIColor(client.rssi) : '#ef4444';

                                const polyline = L.polyline(
                                    [[clientLat, clientLng], apCoords],
                                    {
                                        color: linkColor,
                                        weight: isLinkOnline ? 3 : 2,
                                        opacity: isLinkOnline ? 0.85 : 0.45,
                                        dashArray: isLinkOnline ? null : '6, 6'
                                    }
                                ).addTo(markersLayerRef.current);

                                polyline.bindTooltip(`
                                    <div style="font-family: 'Outfit', sans-serif; padding: 4px;">
                                        <strong style="color: ${linkColor}">Enlace Ubiquiti: ${client.fullName}</strong><br/>
                                        Señal Real: <b>${client.rssi ? client.rssi + ' dBm' : 'Sin Registros'}</b><br/>
                                        ${client.latency !== null ? `Latencia: <b>${client.latency} ms</b><br/>` : ''}
                                        ${client.ccq ? `CCQ: <b>${client.ccq}%</b><br/>` : ''}
                                        Distancia: <b>${client.distance} km</b>
                                    </div>
                                `, { sticky: true });
                            }
                        }
                    });
                    break;
                }

                case 'installations':
                    data.forEach(installation => {
                        if (installation.coordinates) {
                            const [lat, lng] = parseCoordinates(installation.coordinates);

                            if (lat && lng) {
                                counts[installation.status] = (counts[installation.status] || 0) + 1;

                                let icon = pendingInstallationIcon;
                                if (installation.status === 'completed') icon = clientActiveIcon;
                                if (installation.status === 'cancelled') icon = cancelledInstallationIcon;

                                const marker = L.marker([lat, lng], { icon })
                                    .addTo(markersLayerRef.current)
                                    .bindPopup(`
                                        <div style="font-family: 'Outfit', sans-serif; padding: 6px;">
                                            <h3 style="margin: 0 0 6px 0; color: #1a202c; font-size: 14px; font-weight: 600;">${installation.client_name}</h3>
                                            <p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>Fecha:</strong> ${new Date(installation.installation_date).toLocaleDateString()}</p>
                                            <p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>Técnico:</strong> ${installation.technician}</p>
                                            <p style="margin: 3px 0; font-size: 12px;"><strong>Estado:</strong> 
                                                <span style="color: ${
                                                    installation.status === 'pending' ? '#f59e0b' : 
                                                    installation.status === 'completed' ? '#10b981' : '#7f8c8d'
                                                }; font-weight: 600;">
                                                    ${
                                                        installation.status === 'pending' ? 'Pendiente' :
                                                        installation.status === 'completed' ? 'Completada' : 'Cancelada'
                                                    }
                                                </span>
                                            </p>
                                            <p style="margin: 3px 0; font-size: 12px; color: #4a5568; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 200px;"><strong>Dirección:</strong> ${installation.address}</p>
                                        </div>
                                    `);

                                if (installation.id) markerMapRef.current.set(String(installation.id), marker);
                                markerMapRef.current.set(`${lat.toFixed(5)},${lng.toFixed(5)}`, marker);

                                if (onMarkerClick) {
                                    marker.on('click', () => onMarkerClick(installation));
                                }
                            }
                        }
                    });
                    break;

                case 'coverage':
                    data.forEach(ap => {
                        if (ap.coordinates) {
                            const [lat, lng] = parseCoordinates(ap.coordinates);

                            if (lat && lng) {
                                counts[ap.type] = (counts[ap.type] || 0) + 1;

                                const azimuthText = ap.azimuth !== null && ap.azimuth !== undefined ? `${ap.azimuth}°` : '0°';
                                const beamwidthText = ap.beamwidth !== null && ap.beamwidth !== undefined ? `${ap.beamwidth}°` : '90°';

                                const typeLabels = {
                                    sectorial: 'AP Sectorial',
                                    omnidireccional: 'AP Omnidireccional',
                                    ptp: 'Enlace PTP (Backhaul)',
                                    repetidor: 'Repetidor'
                                };
                                const apType = typeLabels[ap.type] || ap.type;

                                const marker = L.marker([lat, lng], { icon: accessPointIcon })
                                    .addTo(markersLayerRef.current)
                                    .bindPopup(`
                                        <div style="font-family: 'Outfit', sans-serif; padding: 6px;">
                                            <h3 style="margin: 0 0 6px 0; color: #3f51b5; font-size: 14px; font-weight: 600;">AP: ${ap.name}</h3>
                                            <p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>Tipo:</strong> ${apType}</p>
                                            <p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>IP:</strong> ${ap.ip_address || ap.ip || 'N/A'}</p>
                                            <p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>Modelo:</strong> ${ap.model || 'N/A'}</p>
                                            <p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>Clientes Conectados:</strong> ${ap.connected_clients || 0}</p>
                                            <p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>Radio de Cobertura:</strong> ${ap.coverage_radius || 500}m</p>
                                            <p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>Rumbo (Azimut):</strong> ${azimuthText}</p>
                                            <p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>Apertura de Haz:</strong> ${beamwidthText}</p>
                                            <p style="margin: 3px 0; font-size: 12px; color: #4a5568; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 200px;"><strong>Ubicación:</strong> ${ap.address || 'N/A'}</p>
                                        </div>
                                    `);

                                if (ap.id) markerMapRef.current.set(String(ap.id), marker);
                                markerMapRef.current.set(`${lat.toFixed(5)},${lng.toFixed(5)}`, marker);

                                const beam = ap.beamwidth !== undefined && ap.beamwidth !== null ? parseInt(ap.beamwidth) : 90;
                                const az = ap.azimuth !== undefined && ap.azimuth !== null ? parseInt(ap.azimuth) : 0;

                                if (beam >= 360) {
                                    // Cobertura Omnidireccional
                                    L.circle([lat, lng], {
                                        radius: ap.coverage_radius || 500,
                                        fillColor: '#3f51b5',
                                        fillOpacity: 0.15,
                                        color: '#3f51b5',
                                        weight: 1.5,
                                        dashArray: '4, 6'
                                    }).addTo(markersLayerRef.current);
                                } else {
                                    // Cobertura Sectorial real (abanico trigonométrico)
                                    L.polygon(getSectorPoints([lat, lng], ap.coverage_radius || 500, az, beam), {
                                        fillColor: '#3f51b5',
                                        fillOpacity: 0.18,
                                        color: '#3f51b5',
                                        weight: 2,
                                        dashArray: '3, 4'
                                    }).addTo(markersLayerRef.current);
                                }

                                if (onMarkerClick) {
                                    marker.on('click', () => onMarkerClick(ap));
                                }
                            }
                        }
                    });
                    break;

                case 'network':
                    data.forEach(link => {
                        if (link.source_coordinates && link.target_coordinates) {
                            const [srcLat, srcLng] = parseCoordinates(link.source_coordinates);
                            const [tgtLat, tgtLng] = parseCoordinates(link.target_coordinates);

                            if (srcLat && srcLng && tgtLat && tgtLng) {
                                counts[link.type] = (counts[link.type] || 0) + 1;

                                const isPtP = link.type === 'backhaul_ptp' || !!link.master_ip;
                                const linkColor = isPtP ? '#0284c7' : (link.status === 'active' || link.status === 'online' ? '#10b981' : '#ef4444');

                                const polyline = L.polyline(
                                    [[srcLat, srcLng], [tgtLat, tgtLng]],
                                    {
                                        color: linkColor,
                                        weight: isPtP ? 5 : 3.5,
                                        opacity: 0.9,
                                        dashArray: isPtP ? '8, 8' : (link.type === 'wireless' ? '6, 8' : null)
                                    }
                                ).addTo(markersLayerRef.current);

                                polyline.bindPopup(`
                                    <div style="font-family: 'Outfit', sans-serif; padding: 6px; min-width: 220px;">
                                        <h3 style="margin: 0 0 6px 0; color: #0284c7; font-size: 14px; font-weight: 700;">⚡ ${link.name || 'Enlace Troncal PtP'}</h3>
                                        <p style="margin: 3px 0; font-size: 12px; color: #334155;"><strong>Nodos:</strong> ${link.source_name || 'Nodo Origen'} ➔ ${link.target_name || 'Nodo Destino'}</p>
                                        ${link.radio_model ? `<p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>Equipo:</strong> ${link.radio_model}</p>` : ''}
                                        ${link.master_ip ? `<p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>Radio Master IP:</strong> <code>${link.master_ip}</code></p>` : ''}
                                        ${link.station_ip ? `<p style="margin: 3px 0; font-size: 12px; color: #4a5568;"><strong>Radio Station IP:</strong> <code>${link.station_ip}</code></p>` : ''}
                                        ${link.target_subnet ? `<p style="margin: 3px 0; font-size: 12px; color: #0369a1;"><strong>Subred Alimentada:</strong> <b>${link.target_subnet}</b></p>` : ''}
                                        <p style="margin: 3px 0; font-size: 12px;"><strong>Estado:</strong> 
                                            <span style="color: ${link.status === 'online' || link.status === 'active' ? '#10b981' : '#ef4444'}; font-weight: 700;">
                                                ${link.status === 'online' || link.status === 'active' ? '🟢 En línea' : '🔴 Caído'}
                                                ${link.latency_ms ? ` (${link.latency_ms} ms)` : ''}
                                            </span>
                                        </p>
                                    </div>
                                `);

                                L.marker([srcLat, srcLng], { icon: accessPointIcon })
                                    .addTo(markersLayerRef.current)
                                    .bindPopup(`<div style="font-family: 'Outfit', sans-serif;"><h3>Nodo Origen: ${link.source_name || 'Nodo Origen'}</h3></div>`);

                                L.marker([tgtLat, tgtLng], { icon: accessPointIcon })
                                    .addTo(markersLayerRef.current)
                                    .bindPopup(`<div style="font-family: 'Outfit', sans-serif;"><h3>Nodo Destino: ${link.target_name || 'Nodo Destino'}</h3></div>`);
                            }
                        }
                    });
                    break;
            }

            setStatusCounts(counts);

            // Ajustar el zoom automático para abarcar todos los elementos mapeados de manera elegante
            if (markersLayerRef.current.getLayers().length > 0) {
                const group = new L.featureGroup(markersLayerRef.current.getLayers());
                mapInstanceRef.current.fitBounds(group.getBounds(), { padding: [40, 40] });
            }
        } catch (error) {
            console.error('Error loading map data:', error);
            setError('Error al cargar datos del mapa: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleZoneChange = (e) => {
        setSelectedZone(e.target.value);
    };

    const handleFilterChange = (e) => {
        setFilterType(e.target.value);
    };

    const handleRefresh = () => {
        loadMarkers();
    };

    const handleGetCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;

                    if (mode === 'selection' && onCoordinateSelect) {
                        onCoordinateSelect(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
                    }

                    if (mapInstanceRef.current) {
                        mapInstanceRef.current.flyTo([latitude, longitude], 16);

                        // Crear marcador temporal con animación brillante
                        L.marker([latitude, longitude], {
                            icon: L.divIcon({
                                className: 'pulse-icon',
                                html: '<div class="client-active-pulse" style="width: 24px; height: 24px; background: rgba(16,185,129,0.3); border: 2.5px solid #10b981; border-radius: 50%; display: flex; align-items: center; justify-content: center;"><div style="width: 10px; height: 10px; background: #10b981; border-radius: 50%;"></div></div>',
                                iconSize: [24, 24],
                                iconAnchor: [12, 12]
                            })
                        }).addTo(mapInstanceRef.current)
                            .bindPopup('Tu ubicación actual aproximada')
                            .openPopup();
                    }
                },
                (error) => {
                    console.error('Error getting location:', error);
                    setError('No se pudo obtener la ubicación actual: ' + (error.message || 'Permiso denegado'));
                }
            );
        } else {
            setError('La geolocalización no está soportada por su navegador');
        }
    };

    const renderMapTitle = () => {
        switch (mode) {
            case 'clients':
                return 'Mapa de Clientes';
            case 'installations':
                return 'Mapa de Instalaciones';
            case 'coverage':
                return 'Mapa de Cobertura de APs';
            case 'network':
                return 'Topología y Enlaces de Red';
            case 'selection':
                return 'Ubicación Geográfica';
            default:
                return 'Mapa';
        }
    };

    const renderStatusChips = () => {
        if (Object.keys(statusCounts).length === 0) return null;

        return (
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {Object.entries(statusCounts).map(([status, count]) => (
                    <Chip
                        key={status}
                        label={`${
                            status === 'active' ? 'Activos' :
                            status === 'inactive' ? 'Inactivos' :
                            status === 'pending' ? 'Pendientes' :
                            status === 'completed' ? 'Completadas' :
                            status === 'cancelled' ? 'Canceladas' :
                            status === 'sectorial' ? 'AP Sectorial' :
                            status === 'omnidireccional' ? 'AP Omnidireccional' :
                            status === 'ptp' ? 'Enlace PTP' :
                            status === 'repetidor' ? 'Repetidores' :
                            status
                        }: ${count}`}
                        color={
                            status === 'active' || status === 'completed' ? 'success' :
                            status === 'inactive' || status === 'cancelled' ? 'error' :
                            status === 'pending' ? 'warning' :
                            ['sectorial', 'omnidireccional', 'ptp', 'repetidor'].includes(status) ? 'primary' : 'default'
                        }
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: "'Outfit', sans-serif" }}
                    />
                ))}
            </Box>
        );
    };

    return (
        <Paper 
            ref={containerRef}
            elevation={hideHeader ? 0 : (isFullscreen ? 0 : 2)} 
            sx={{ 
                p: hideHeader ? 0 : (isFullscreen ? 3 : 2), 
                height: isFullscreen ? '100vh' : (hideHeader ? height : 'auto'), 
                width: '100%',
                flex: 1, // MODIFICADO: Crecer en flex containers
                borderRadius: isFullscreen ? 0 : (hideHeader ? 0 : 2),
                border: isFullscreen ? 'none' : (hideHeader ? 'none' : undefined),
                background: isFullscreen ? '#ffffff' : (hideHeader ? 'transparent' : undefined),
                boxShadow: isFullscreen ? 'none' : undefined,
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                zIndex: isFullscreen ? 9999 : undefined
            }}
        >
            {!hideHeader && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                    <Typography variant="h6" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                        <MapIcon sx={{ mr: 1, color: 'primary.main' }} />
                        {renderMapTitle()}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        {mode !== 'selection' && (
                            <>
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <InputLabel sx={{ fontFamily: "'Outfit', sans-serif" }}>Zona</InputLabel>
                                    <Select
                                        value={selectedZone}
                                        onChange={handleZoneChange}
                                        label="Zona"
                                        sx={{ borderRadius: 1.5, fontFamily: "'Outfit', sans-serif" }}
                                    >
                                        <MenuItem value="">Todas las Zonas</MenuItem>
                                        {zones.map(zone => (
                                            <MenuItem key={zone.id} value={zone.id}>{zone.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {(mode === 'clients' || mode === 'installations') && (
                                    <FormControl size="small" sx={{ minWidth: 140 }}>
                                        <InputLabel sx={{ fontFamily: "'Outfit', sans-serif" }}>Estado</InputLabel>
                                        <Select
                                            value={filterType}
                                            onChange={handleFilterChange}
                                            label="Estado"
                                            startAdornment={<FilterIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />}
                                            sx={{ borderRadius: 1.5, fontFamily: "'Outfit', sans-serif" }}
                                        >
                                            <MenuItem value="all">Todos</MenuItem>
                                            {mode === 'clients' && (
                                                <>
                                                    <MenuItem value="active">Activos</MenuItem>
                                                    <MenuItem value="inactive">Inactivos</MenuItem>
                                                </>
                                            )}
                                            {mode === 'installations' && (
                                                <>
                                                    <MenuItem value="pending">Pendientes</MenuItem>
                                                    <MenuItem value="completed">Completadas</MenuItem>
                                                    <MenuItem value="cancelled">Canceladas</MenuItem>
                                                </>
                                            )}
                                        </Select>
                                    </FormControl>
                                )}
                            </>
                        )}

                        <Tooltip title="Actualizar mapa">
                            <IconButton onClick={handleRefresh} color="primary" sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 2 }}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Usar mi ubicación actual">
                            <IconButton onClick={handleGetCurrentLocation} color="primary" sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 2 }}>
                                <MyLocationIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5, fontFamily: "'Outfit', sans-serif" }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {!hideHeader && renderStatusChips()}

            <Box
                sx={{
                    position: 'relative',
                    flex: 1,
                    height: hideHeader ? '100%' : (isFullscreen ? '100%' : height),
                    width: '100%',
                    border: isFullscreen ? 'none' : (hideHeader ? 'none' : '1px solid rgba(0,0,0,0.08)'),
                    borderRadius: isFullscreen ? 0 : (hideHeader ? 0 : 2),
                    boxShadow: isFullscreen ? 'none' : (hideHeader ? 'none' : '0px 4px 20px rgba(0,0,0,0.03)'),
                    mt: isFullscreen ? 0 : (hideHeader ? 0 : 2),
                    overflow: 'hidden'
                }}
            >
                <div
                    ref={mapRef}
                    style={{
                        height: '100%',
                        width: '100%',
                        zIndex: 0
                    }}
                />

                {/* Botón Flotante de Pantalla Completa - Estilo nativo Leaflet */}
                <Tooltip title={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}>
                    <IconButton
                        onClick={toggleFullscreen}
                        sx={{
                            position: 'absolute',
                            top: 80, // Justo debajo del control de zoom de Leaflet
                            left: 10,
                            zIndex: 1000,
                            backgroundColor: 'white',
                            border: '2px solid rgba(0,0,0,0.2)',
                            borderRadius: '4px',
                            width: '30px',
                            height: '30px',
                            padding: '4px',
                            boxShadow: '0 1px 5px rgba(0,0,0,0.4)',
                            color: '#555',
                            '&:hover': {
                                backgroundColor: '#f4f4f4',
                                color: '#000'
                            }
                        }}
                    >
                        {isFullscreen ? <FullscreenExitIcon sx={{ fontSize: 20 }} /> : <FullscreenIcon sx={{ fontSize: 20 }} />}
                    </IconButton>
                </Tooltip>

                {loading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.75)',
                            backdropFilter: 'blur(2px)',
                            zIndex: 1
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
            </Box>

            {mode === 'selection' && !hideHeader && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'text.secondary', fontFamily: "'Outfit', sans-serif", fontSize: '11.5px' }}>
                    💡 <strong>Consejo:</strong> Haz clic en cualquier lugar del mapa para colocar el marcador, o arrastra la mira telescópica para calibrar finamente el punto.
                </Typography>
            )}
        </Paper>
    );
};

export default GeographicMap;